import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  TextField,
  Slider,
  Accordion,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Book as BookIcon,
  School as SchoolIcon,
  TableChart as TableChartIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Assessment as AssessmentIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Grade, Student, Course } from '../types';
import { getGrades, getStudentGrades, getCourseGrades, deleteGrade } from '../services/gradeService';
import { getStudents } from '../services/studentService';
import { getCourses } from '../services/courseService';
import { getExams } from '../services/examService';
import api from '../api/api';
import AddGradeDialog from '../components/AddGradeDialog';
import EditGradeDialog from '../components/EditGradeDialog';
import StudentGradeSummaryDialog from '../components/GradeSummaryDialog';
import ExamGradeSummaryDialog from '../components/ExamGradeSummaryDialog';

const Grades: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [grades, setGrades] = useState<Grade[]>([]);
  const [students, setStudents] = useState<Student[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [exams, setExams] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState<'all' | 'student' | 'course'>('all');
  const [selectedStudent, setSelectedStudent] = useState<number | ''>('');
  const [selectedCourse, setSelectedCourse] = useState<number | ''>('');
  
  // Enhanced filter states
  const [gradeTypeFilter, setGradeTypeFilter] = useState<string>('');
  const [classLevelFilter, setClassLevelFilter] = useState<string>('');
  const [examFilter, setExamFilter] = useState<number | ''>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [gradeRange, setGradeRange] = useState<[number, number]>([0, 100]);
  const [percentageRange, setPercentageRange] = useState<[number, number]>([0, 100]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [summaryDialogOpen, setSummaryDialogOpen] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState<Grade | null>(null);
  const [selectedStudentForSummary, setSelectedStudentForSummary] = useState<number | null>(null);
  const [selectedExamForSummary, setSelectedExamForSummary] = useState<number | null>(null);
  
  // Exam summary dialog states
  const [examSummaryDialogOpen, setExamSummaryDialogOpen] = useState(false);
  const [selectedExamIdForSummary, setSelectedExamIdForSummary] = useState<number | null>(null);

  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [importError, setImportError] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const isSuperUser = user?.role === 'superuser';

  // Class levels for filtering
  const classLevels = [
    'Pre-Primary 1', 'Pre-Primary 2',
    'Grade 1', 'Grade 2', 'Grade 3', 'Grade 4', 'Grade 5',
    'Grade 6', 'Grade 7', 'Grade 8',
    'Grade 9', 'Grade 10', 'Grade 11', 'Grade 12'
  ];

  // Grade types for filtering
  const gradeTypes = ['exam', 'homework', 'quiz', 'assignment', 'project'];

  // Fetch paginated grades from backend
  const fetchPaginatedGrades = useCallback(async () => {
    if (!user?.school_id) {
      setError('School ID not available');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const params = new URLSearchParams({
        skip: String(page * rowsPerPage),
        limit: String(rowsPerPage)
      });
      const response = await api.get(`/grades/?${params.toString()}`);
      const data = response.data as { grades: Grade[]; total: number };
      setGrades(data.grades || []);
      setTotalCount(data.total || 0);
    } catch (err: any) {
      setError('Failed to load grades');
      setGrades([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [user?.school_id, page, rowsPerPage]);

  useEffect(() => {
    fetchPaginatedGrades();
  }, [fetchPaginatedGrades]);

  // Reset page when grades change
  useEffect(() => {
    setPage(0);
  }, [grades]);

  const handleFilterChange = async () => {
    try {
      setLoading(true);
      let gradesData: Grade[] = [];

      // Get base data based on filter type
      if (filterType === 'student' && selectedStudent) {
        gradesData = await getStudentGrades(selectedStudent as number);
      } else if (filterType === 'course' && selectedCourse) {
        gradesData = await getCourseGrades(selectedCourse as number);
      } else {
        gradesData = await getGrades();
      }

      // Apply additional filters
      // Create a student lookup map for efficient filtering
      const studentLookup = new Map(students.map(student => [student.id, student]));
      
      gradesData = gradesData.filter(grade => {
        // Grade type filter
        if (gradeTypeFilter && grade.grade_type !== gradeTypeFilter) {
          return false;
        }

        // Class level filter (if we have student data)
        if (classLevelFilter) {
          const student = studentLookup.get(grade.student_id);
          if (!student || student.class_level !== classLevelFilter) {
            return false;
          }
        }

        // Exam filter
        if (examFilter && grade.exam_id !== examFilter) {
          return false;
        }

        // Date range filter
        if (dateFrom || dateTo) {
          const gradeDate = grade.graded_at ? new Date(grade.graded_at) : new Date(grade.created_at);
          if (dateFrom && gradeDate < new Date(dateFrom)) {
            return false;
          }
          if (dateTo && gradeDate > new Date(dateTo)) {
            return false;
          }
        }

        // Grade range filter
        const gradePercentage = (grade.grade / grade.max_grade) * 100;
        if (gradePercentage < gradeRange[0] || gradePercentage > gradeRange[1]) {
          return false;
        }

        // Percentage range filter
        if (gradePercentage < percentageRange[0] || gradePercentage > percentageRange[1]) {
          return false;
        }

        return true;
      });

      setGrades(gradesData);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to filter grades');
    } finally {
      setLoading(false);
    }
  };

  const clearAllFilters = () => {
    setFilterType('all');
    setSelectedStudent('');
    setSelectedCourse('');
    setGradeTypeFilter('');
    setClassLevelFilter('');
    setExamFilter('');
    setDateFrom('');
    setDateTo('');
    setGradeRange([0, 100]);
    setPercentageRange([0, 100]);
    fetchPaginatedGrades();
  };

  const handleDeleteGrade = async (gradeId: number) => {
    if (window.confirm('Are you sure you want to delete this grade?')) {
      try {
        await deleteGrade(gradeId);
        setGrades(grades.filter(grade => grade.id !== gradeId));
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Failed to delete grade');
      }
    }
  };

  const handleEditGrade = (grade: Grade) => {
    setSelectedGrade(grade);
    setEditDialogOpen(true);
  };

  const handleViewSummary = (studentId: number, examId: number | null) => {
    setSelectedStudentForSummary(studentId);
    setSelectedExamForSummary(examId);
    setSummaryDialogOpen(true);
  };

  const handleViewExamSummary = (examId: number) => {
    setSelectedExamIdForSummary(examId);
    setExamSummaryDialogOpen(true);
  };

  const getGradeColor = (grade: number, maxGrade: number) => {
    const percentage = (grade / maxGrade) * 100;
    if (percentage >= 80) return 'success';
    if (percentage >= 60) return 'warning';
    return 'error';
  };

  const getGradeTypeColor = (type: string) => {
    switch (type.toLowerCase()) {
      case 'exam': return 'error';
      case 'homework': return 'info';
      case 'quiz': return 'warning';
      default: return 'default';
    }
  };

  const examMap = new Map(exams.map(exam => [exam.id, exam.title]));

  const handleOpenImportDialog = () => {
    setImportDialogOpen(true);
    setImportFile(null);
    setImportResult(null);
    setImportError('');
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    setImportFile(null);
    setImportResult(null);
    setImportError('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImportFile(e.target.files[0]);
    }
  };

  const handleDownloadTemplate = () => {
    const csv = `# You may use either student_id (system ID) or student_code (school-given ID), and either course_id (system ID) or course_code (course code). Do not fill both for the same row.\nstudent_id,student_code,course_id,course_code,exam_id,assignment_name,grade,max_grade,grade_type,comments,submitted_at,graded_at\n,,2,,5,Midterm,78,100,exam,Good,2024-06-01,2024-06-02\n,ADM-001,,,5,Midterm,88,100,exam,Excellent,2024-06-01,2024-06-02\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'grade_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportResult(null);
    setImportError('');
    try {
      const formData = new FormData();
      formData.append('file', importFile);
      const baseUrl = (window as any).apiBaseUrl || process.env.REACT_APP_API_URL || window.location.origin;
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(`${baseUrl}/grades/bulk/import`, {
        method: 'POST',
        headers,
        body: formData
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Import failed');
      setImportResult(data);
      fetchPaginatedGrades();
    } catch (err: any) {
      setImportError(err.message || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SchoolIcon /> Grade Management
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<CloudUploadIcon />}
            onClick={handleOpenImportDialog}
          >
            Bulk Import Grades
          </Button>
          <Button
            variant="outlined"
            startIcon={<TableChartIcon />}
            onClick={() => navigate('/bulk-grading')}
          >
            Bulk Grade Entry
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Grade
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{typeof error === 'string' ? error : JSON.stringify(error)}</Alert>}

      {/* Enhanced Filter Controls */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterIcon /> Filters
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                startIcon={<FilterIcon />}
              >
                {showAdvancedFilters ? 'Hide' : 'Show'} Advanced Filters
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={clearAllFilters}
                startIcon={<ClearIcon />}
                color="error"
              >
                Clear All
              </Button>
            </Box>
          </Box>

          {/* Basic Filters */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
              <FormControl fullWidth>
                <InputLabel>Filter Type</InputLabel>
                <Select
                  value={filterType}
                  label="Filter Type"
                  onChange={(e) => setFilterType(e.target.value as any)}
                >
                  <MenuItem value="all">All Grades</MenuItem>
                  <MenuItem value="student">By Student</MenuItem>
                  <MenuItem value="course">By Course</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            {filterType === 'student' && (
              <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
                <FormControl fullWidth>
                  <InputLabel>Select Student</InputLabel>
                  <Select
                    value={selectedStudent}
                    label="Select Student"
                    onChange={(e) => setSelectedStudent(e.target.value)}
                  >
                    {students.map((student) => (
                      <MenuItem key={student.id} value={student.id}>
                        {student.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {filterType === 'course' && (
              <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
                <FormControl fullWidth>
                  <InputLabel>Select Course</InputLabel>
                  <Select
                    value={selectedCourse}
                    label="Select Course"
                    onChange={(e) => setSelectedCourse(e.target.value)}
                  >
                    {courses.map((course) => (
                      <MenuItem key={course.id} value={course.id}>
                        {course.title}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
              <FormControl fullWidth>
                <InputLabel>Grade Type</InputLabel>
                <Select
                  value={gradeTypeFilter}
                  label="Grade Type"
                  onChange={(e) => setGradeTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All Types</MenuItem>
                  {gradeTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
              <FormControl fullWidth>
                <InputLabel>Class Level</InputLabel>
                <Select
                  value={classLevelFilter}
                  label="Class Level"
                  onChange={(e) => setClassLevelFilter(e.target.value)}
                >
                  <MenuItem value="">All Levels</MenuItem>
                  {classLevels.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
              <Button
                variant="contained"
                onClick={handleFilterChange}
                disabled={loading}
                fullWidth
              >
                Apply Filters
              </Button>
            </Box>
          </Box>

          {/* Advanced Filters */}
          {showAdvancedFilters && (
            <Accordion expanded={showAdvancedFilters} sx={{ mt: 2 }}>
              <AccordionDetails>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
                    <FormControl fullWidth>
                      <InputLabel>Exam</InputLabel>
                      <Select
                        value={examFilter}
                        label="Exam"
                        onChange={(e) => setExamFilter(e.target.value as number)}
                      >
                        <MenuItem value="">All Exams</MenuItem>
                        {exams.map((exam) => (
                          <MenuItem key={exam.id} value={exam.id}>
                            {exam.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>

                  <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
                    <TextField
                      fullWidth
                      label="Date From"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 250px', minWidth: 250 }}>
                    <TextField
                      fullWidth
                      label="Date To"
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Box>

                  <Box sx={{ flex: '1 1 400px', minWidth: 400 }}>
                    <Typography gutterBottom>Grade Range (%)</Typography>
                    <Slider
                      value={gradeRange}
                      onChange={(_, newValue) => setGradeRange(newValue as [number, number])}
                      valueLabelDisplay="auto"
                      min={0}
                      max={100}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption">{gradeRange[0]}%</Typography>
                      <Typography variant="caption">{gradeRange[1]}%</Typography>
                    </Box>
                  </Box>

                  <Box sx={{ flex: '1 1 400px', minWidth: 400 }}>
                    <Typography gutterBottom>Percentage Range (%)</Typography>
                    <Slider
                      value={percentageRange}
                      onChange={(_, newValue) => setPercentageRange(newValue as [number, number])}
                      valueLabelDisplay="auto"
                      min={0}
                      max={100}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="caption">{percentageRange[0]}%</Typography>
                      <Typography variant="caption">{percentageRange[1]}%</Typography>
                    </Box>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          )}
        </CardContent>
      </Card>

      {/* Results Summary */}
      {!loading && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                Results: {grades.length} grade{grades.length !== 1 ? 's' : ''} found
              </Typography>
              {grades.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Chip 
                    label={`Avg: ${(grades.reduce((sum, grade) => sum + (grade.grade / grade.max_grade) * 100, 0) / grades.length).toFixed(1)}%`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip 
                    label={`Highest: ${Math.max(...grades.map(grade => (grade.grade / grade.max_grade) * 100)).toFixed(1)}%`}
                    color="success"
                    variant="outlined"
                  />
                  <Chip 
                    label={`Lowest: ${Math.min(...grades.map(grade => (grade.grade / grade.max_grade) * 100)).toFixed(1)}%`}
                    color="error"
                    variant="outlined"
                  />
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell>Course</TableCell>
              <TableCell>Details</TableCell>
              <TableCell>Grade</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Percentage</TableCell>
              <TableCell>Graded Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {grades.map((grade) => (
              <TableRow key={grade.id} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon fontSize="small" />
                    {grade.student_name || `Student ${grade.student_id}`}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BookIcon fontSize="small" />
                    {grade.course_title || `Course ${grade.course_id}`}
                  </Box>
                </TableCell>
                <TableCell>
                  {grade.exam_id ? (
                    <Chip
                      icon={<AssessmentIcon />}
                      label={examMap.get(grade.exam_id) || `Exam ${grade.exam_id}`}
                      onClick={() => handleViewExamSummary(grade.exam_id!)}
                      size="small"
                      color="secondary"
                    />
                  ) : (
                    grade.assignment_name
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${grade.grade}/${grade.max_grade}`}
                    color={getGradeColor(grade.grade, grade.max_grade)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={grade.grade_type}
                    color={getGradeTypeColor(grade.grade_type)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {((grade.grade / grade.max_grade) * 100).toFixed(1)}%
                </TableCell>
                <TableCell>
                  {grade.graded_at ? new Date(grade.graded_at).toLocaleDateString() : 'Not graded'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip title="View Student Summary">
                      <IconButton onClick={() => handleViewSummary(grade.student_id, grade.exam_id ?? null)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    {grade.exam_id && (
                      <Tooltip title="View Exam Summary">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewExamSummary(grade.exam_id!)}
                        >
                          <AssessmentIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Edit Grade">
                      <IconButton
                        size="small"
                        onClick={() => handleEditGrade(grade)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Grade">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteGrade(grade.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={e => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
        </>
      )}

      {/* Add Grade Dialog */}
      <AddGradeDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onSuccess={() => {
          setAddDialogOpen(false);
          fetchPaginatedGrades();
        }}
      />

      {/* Edit Grade Dialog */}
      {selectedGrade && (
        <EditGradeDialog
          open={editDialogOpen}
          onClose={() => setEditDialogOpen(false)}
          onSuccess={() => {
            setEditDialogOpen(false);
            setSelectedGrade(null);
            fetchPaginatedGrades();
          }}
          grade={selectedGrade}
          students={students}
          courses={courses}
        />
      )}

      {/* Grade Summary Dialog */}
      {selectedStudentForSummary && (
        <StudentGradeSummaryDialog
          open={summaryDialogOpen}
          onClose={() => setSummaryDialogOpen(false)}
          studentId={selectedStudentForSummary}
          studentName={students.find(s => s.id === selectedStudentForSummary)?.name || ''}
          examId={selectedExamForSummary ?? undefined}
        />
      )}

      {/* Exam Grade Summary Dialog */}
      {selectedExamIdForSummary && (
        <ExamGradeSummaryDialog
          open={examSummaryDialogOpen}
          onClose={() => setExamSummaryDialogOpen(false)}
          examId={selectedExamIdForSummary}
        />
      )}

      <Dialog open={importDialogOpen} onClose={handleCloseImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Import Grades</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="info">
              You may use either <b>student_id</b> (system ID) or <b>student_code</b> (school-given ID), and either <b>course_id</b> (system ID) or <b>course_code</b> (course code).<br />
              <b>Do not fill both for the same row.</b> The system will use the ID if both are present.<br />
              This helps avoid confusion if your school-given IDs overlap with system IDs.
            </Alert>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
            >
              Download CSV Template
            </Button>
            <input
              type="file"
              accept=".csv,.xlsx,.xls,.json"
              onChange={handleFileChange}
              style={{ marginTop: 16 }}
            />
            {importing && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={20} /> Importing...</Box>}
            {importError && <Alert severity="error">{importError}</Alert>}
            {importResult && (
              <Alert severity={importResult.success ? 'success' : 'warning'}>
                Imported: {importResult.imported} grade(s).<br />
                {importResult.errors && importResult.errors.length > 0 && (
                  <Box>
                    <strong>Errors:</strong>
                    <ul>
                      {importResult.errors.map((err: any, idx: number) => (
                        <li key={idx}>Row {err.row}: {err.error}</li>
                      ))}
                    </ul>
                  </Box>
                )}
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseImportDialog}>Cancel</Button>
          <Button
            onClick={handleImport}
            variant="contained"
            disabled={!importFile || importing}
            startIcon={<CloudUploadIcon />}
          >
            Import
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Grades; 